import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useInView, useMotionValue, animate as motionAnimate } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ScanLine,
  BellRing,
  MessageCircle,
  TrendingUp,
  Users,
  Download,
  Camera,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Leaf,
  Store,
  Apple,
  ShoppingBasket,
  Beef,
  Wheat,
} from "lucide-react";
import HeroScanAnimation from "@/components/landing/HeroScanAnimation";

const DELI_IMAGE =
  "https://images.pexels.com/photos/29570761/pexels-photo-29570761.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "InPalmo — Il magazzino, in palmo di mano.";
  }, []);

  const goGuest = () => {
    localStorage.setItem("mag_guest", "1");
    navigate("/app/dashboard");
  };
  const goLogin = () => {
    navigate("/entra");
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#FAF9F6] text-[#1A1C19]">
      <Nav onGuest={goGuest} onLogin={goLogin} />
      <Hero onGuest={goGuest} onLogin={goLogin} />
      <ProblemStats />
      <HowItWorks />
      <Features />
      <Audience />
      <Pricing onLogin={goLogin} />
      <Faq />
      <Footer />
    </div>
  );
}

/* ---------------- Brand Mark ---------------- */
function BrandMark({ size = "md" }) {
  const text = size === "lg" ? "text-2xl" : "text-xl";
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-9 w-9 rounded-xl bg-[#2E4A3B] text-white flex items-center justify-center shadow-sm">
        <Leaf size={18} className="text-[#A8D4B8]" />
      </div>
      <span
        className={`font-semibold tracking-tight ${text}`}
        style={{ fontFamily: "Work Sans, sans-serif" }}
      >
        In<span className="text-[#2E4A3B]">Palmo</span>
      </span>
    </div>
  );
}

/* ---------------- Nav ---------------- */
function Nav({ onGuest, onLogin }) {
  return (
    <header className="sticky top-0 z-40 bg-[#FAF9F6]/85 backdrop-blur-md border-b border-[#E5E3DB]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <BrandMark />
        <nav className="hidden md:flex items-center gap-7 text-sm text-[#4A524C]">
          <a href="#come-funziona" className="hover:text-[#2E4A3B] transition-colors">Come funziona</a>
          <a href="#funzionalita" className="hover:text-[#2E4A3B] transition-colors">Funzionalità</a>
          <a href="#prezzi" className="hover:text-[#2E4A3B] transition-colors">Prezzi</a>
          <a href="#faq" className="hover:text-[#2E4A3B] transition-colors">Domande</a>
        </nav>
        <div className="flex items-center gap-2">
          <button
            data-testid="nav-login-btn"
            onClick={onLogin}
            className="hidden sm:inline-flex text-sm font-medium text-[#4A524C] hover:text-[#2E4A3B] px-3 py-2 transition-colors"
          >
            Accedi
          </button>
          <Button
            data-testid="nav-guest-btn"
            onClick={onGuest}
            className="bg-[#2E4A3B] hover:bg-[#243A2E] text-white rounded-xl px-4 h-10 text-sm"
          >
            Prova ora
          </Button>
        </div>
      </div>
    </header>
  );
}

/* ---------------- Hero ---------------- */
function Hero({ onGuest, onLogin }) {
  return (
    <section className="relative overflow-hidden">
      {/* Soft sage glow */}
      <div className="absolute inset-x-0 top-0 h-[480px] bg-gradient-to-b from-[#E8F3EC]/60 to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <span
            data-testid="hero-badge"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FDF6E3] border border-[#F2DEB0] text-[#8B6518] text-xs font-semibold uppercase tracking-[0.12em] mb-6"
          >
            <Sparkles size={12} />
            Scansione scontrini con AI
          </span>
          <h1
            data-testid="hero-headline"
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] break-words"
            style={{ fontFamily: "Work Sans, sans-serif" }}
          >
            Il tuo magazzino,
            <br />
            <span className="text-[#2E4A3B]">in palmo</span> di mano.
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-[#4A524C] leading-relaxed max-w-xl">
            Scatta una foto a scontrini e DDT. L&apos;AI riconosce prodotti, quantità e scadenze in
            un attimo. Meno sprechi, più controllo — senza imparare un gestionale.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="hero-cta-guest"
              onClick={onGuest}
              size="lg"
              className="bg-[#2E4A3B] hover:bg-[#243A2E] text-white rounded-xl h-auto min-h-12 py-2 px-6 text-base font-medium whitespace-normal"
            >
              <Camera size={18} className="mr-2" />
              Provalo subito senza registrarti
            </Button>
            <Button
              data-testid="hero-cta-trial"
              onClick={onLogin}
              size="lg"
              variant="outline"
              className="bg-white border-[#2E4A3B] text-[#2E4A3B] hover:bg-[#F4F1ED] rounded-xl h-auto min-h-12 py-2 px-6 text-base font-medium whitespace-normal"
            >
              Inizia un mese gratis
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
          <div className="mt-6 flex items-center gap-4 text-sm text-[#4A524C]">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={14} className="text-[#2E4A3B]" />
              Nessuna carta richiesta
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={14} className="text-[#2E4A3B]" />
              In italiano
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative"
        >
          <HeroScanAnimation />
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Problem stats ---------------- */
function ProblemStats() {
  const stats = [
    { value: 30, suffix: "%", label: "Meno sprechi", body: "Notifiche prima della scadenza per ogni prodotto fresco." },
    { value: 10, suffix: "h", label: "Risparmiate al mese", body: "Niente più serate a ricopiare DDT su Excel o quadernoni." },
    { value: 0, suffix: "", label: "Rotture di stock", body: "Sai sempre cosa manca. Riordini su WhatsApp in un tocco." },
  ];
  return (
    <section className="bg-[#2E4A3B] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 lg:py-24">
        <div className="max-w-2xl">
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-[#D19B34]">
            Perché ne hai bisogno
          </span>
          <h2
            className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight"
            style={{ fontFamily: "Work Sans, sans-serif" }}
          >
            Gestire a occhio ti costa caro.
          </h2>
          <p className="mt-3 text-white/75 text-base sm:text-lg leading-relaxed">
            Tre numeri che descrivono cosa cambia quando smetti di affidarti alla memoria.
          </p>
        </div>
        <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {stats.map((s, i) => (
            <StatCard key={i} {...s} delay={i * 0.1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCard({ value, suffix, label, body, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const display = useMotionValue(0);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = motionAnimate(display, value, {
      duration: 1.4,
      delay,
      ease: "easeOut",
      onUpdate: (v) => setShown(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value, delay, display]);

  return (
    <motion.div
      ref={ref}
      initial={{ y: 16, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.5, delay }}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8"
    >
      <div
        className="text-5xl sm:text-6xl font-semibold tracking-tight text-white"
        style={{ fontFamily: "Work Sans, sans-serif" }}
      >
        {shown}
        <span className="text-[#D19B34]">{suffix}</span>
      </div>
      <div className="mt-3 text-base font-semibold text-white">{label}</div>
      <p className="mt-2 text-sm text-white/70 leading-relaxed">{body}</p>
    </motion.div>
  );
}

/* ---------------- How it works ---------------- */
function HowItWorks() {
  const steps = [
    {
      n: "1",
      title: "Scatta",
      body: "Appoggia lo scontrino o il DDT del fornitore sul bancone. Una foto col telefono, basta.",
      icon: Camera,
      tint: "bg-[#FDF6E3] border-[#F2DEB0] text-[#8B6518]",
    },
    {
      n: "2",
      title: "L'AI lavora per te",
      body: "Riconosce prodotti, distingue pezzi dal peso (kg), legge prezzi, sconti e scadenze.",
      icon: ScanLine,
      tint: "bg-[#E8F3EC] border-[#C5E1D1] text-[#2B5A3F]",
    },
    {
      n: "3",
      title: "Magazzino aggiornato",
      body: "Stock e scadenze pronte all'uso. Se qualcosa sta per scadere, ti avvisiamo prima.",
      icon: CheckCircle2,
      tint: "bg-[#F4ECE6] border-[#E8D8C8] text-[#8B5A3C]",
    },
  ];
  return (
    <section id="come-funziona" className="bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-[#C65D47]">
            Come funziona
          </span>
          <h2
            className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#1A1C19]"
            style={{ fontFamily: "Work Sans, sans-serif" }}
          >
            Ah, ma allora basta una foto?
          </h2>
          <p className="mt-3 text-base sm:text-lg text-[#4A524C] leading-relaxed">
            Esatto. Tre passaggi, meno di un minuto. Funziona anche con scontrini stropicciati.
          </p>
        </div>
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ y: 16, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`step-${s.n}`}
              className="relative bg-white rounded-2xl border border-[#E5E3DB] p-6 sm:p-8 hover:shadow-md transition-shadow"
            >
              <div
                className={`absolute -top-3 left-6 sm:left-8 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border font-mono text-xs font-semibold ${s.tint}`}
              >
                Step {s.n}
              </div>
              <div className={`h-12 w-12 rounded-2xl ${s.tint} flex items-center justify-center mt-2`}>
                <s.icon size={22} />
              </div>
              <h3
                className="mt-5 text-xl font-medium text-[#1A1C19]"
                style={{ fontFamily: "Work Sans, sans-serif" }}
              >
                {s.title}
              </h3>
              <p className="mt-2 text-[15px] text-[#4A524C] leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Features ---------------- */
function Features() {
  const features = [
    {
      icon: ScanLine,
      title: "Scansione AI scontrini e DDT",
      body: "Nessun inserimento manuale. La fotocamera fa tutto: prodotti, prezzi, sconti, scadenze.",
      tone: "bg-[#E8F3EC] text-[#2B5A3F] border-[#C5E1D1]",
      span: "md:col-span-2",
    },
    {
      icon: BellRing,
      title: "Scadenze automatiche",
      body: "Notifiche in-app o email prima che un prodotto scada. Riduci gli scarti.",
      tone: "bg-[#FDF6E3] text-[#8B6518] border-[#F2DEB0]",
    },
    {
      icon: MessageCircle,
      title: "Riordino su WhatsApp",
      body: "Articoli sotto scorta? Mandi l'ordine al fornitore in un tocco, con quantità suggerite.",
      tone: "bg-[#E8F3EC] text-[#2B5A3F] border-[#C5E1D1]",
    },
    {
      icon: TrendingUp,
      title: "Insights di vendita",
      body: "Capisci cosa vende, cosa è fermo, cosa ti fa marginare. Drilldown per ogni KPI.",
      tone: "bg-[#F4ECE6] text-[#8B5A3C] border-[#E8D8C8]",
      span: "md:col-span-2",
    },
    {
      icon: Users,
      title: "Multi-cassiere",
      body: "Cassieri e collaboratori con permessi limitati. Tu vedi tutto, loro solo ciò che serve.",
      tone: "bg-[#E8F3EC] text-[#2B5A3F] border-[#C5E1D1]",
    },
    {
      icon: Download,
      title: "Esportazioni pronte",
      body: "Scarichi prodotti e vendite in CSV o Excel. Per il commercialista o le tue analisi.",
      tone: "bg-[#FDF6E3] text-[#8B6518] border-[#F2DEB0]",
    },
  ];

  return (
    <section id="funzionalita" className="bg-[#F4F1ED]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32">
        <div className="max-w-2xl">
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-[#C65D47]">
            Tutto quello che ti serve
          </span>
          <h2
            className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#1A1C19]"
            style={{ fontFamily: "Work Sans, sans-serif" }}
          >
            Pensato per chi sta dietro al bancone.
          </h2>
          <p className="mt-3 text-base sm:text-lg text-[#4A524C] leading-relaxed">
            Nessuna funzione &quot;decorativa&quot;. Ogni cosa è lì perché serve davvero ogni giorno.
          </p>
        </div>
        <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ y: 16, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
              data-testid={`feature-${i}`}
              className={`bg-white border border-[#E5E3DB] rounded-2xl p-6 sm:p-7 hover:-translate-y-0.5 hover:shadow-md transition-all ${f.span || ""}`}
            >
              <div className={`h-11 w-11 rounded-xl border flex items-center justify-center ${f.tone}`}>
                <f.icon size={20} />
              </div>
              <h3
                className="mt-5 text-lg font-medium text-[#1A1C19]"
                style={{ fontFamily: "Work Sans, sans-serif" }}
              >
                {f.title}
              </h3>
              <p className="mt-2 text-[15px] text-[#4A524C] leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Audience ---------------- */
function Audience() {
  const items = [
    { label: "Alimentari di quartiere", icon: Store },
    { label: "Gastronomie", icon: ShoppingBasket },
    { label: "Salumerie", icon: Beef },
    { label: "Frutta e Verdura", icon: Apple },
    { label: "Mercati rionali", icon: Wheat },
  ];
  return (
    <section className="bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl overflow-hidden aspect-[4/3] lg:aspect-[5/4]"
          >
            <img
              src={DELI_IMAGE}
              alt="Interno di una gastronomia italiana con prodotti freschi"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2E4A3B]/40 via-transparent to-transparent" />
          </motion.div>
          <div>
            <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-[#C65D47]">
              Per chi è
            </span>
            <h2
              className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#1A1C19]"
              style={{ fontFamily: "Work Sans, sans-serif" }}
            >
              Pensato per i negozi di vicinato italiani.
            </h2>
            <p className="mt-3 text-base sm:text-lg text-[#4A524C] leading-relaxed">
              Distinzione pezzi vs peso (kg), categorie alimentari pronte, scadenze a giorni:
              tutto già configurato per come lavori tu.
            </p>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {items.map((it) => (
                <div
                  key={it.label}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[#E5E3DB] rounded-full text-sm font-medium text-[#1A1C19] hover:border-[#2E4A3B] transition-colors"
                >
                  <it.icon size={15} className="text-[#2E4A3B]" />
                  {it.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Pricing ---------------- */
function Pricing({ onLogin }) {
  const items = [
    "Scansioni AI illimitate (scontrini e DDT)",
    "Scadenze, notifiche e riordino fornitori",
    "Insights, drilldown e esportazioni CSV/Excel",
    "Multi-cassiere con permessi",
    "Assistenza via email in italiano",
    "Niente vincoli: disdici quando vuoi",
  ];
  return (
    <section id="prezzi" className="bg-gradient-to-b from-[#FAF9F6] to-[#E8F3EC]/40">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-[#C65D47]">
            Prezzo
          </span>
          <h2
            className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#1A1C19]"
            style={{ fontFamily: "Work Sans, sans-serif" }}
          >
            Semplice, come il tuo lavoro.
          </h2>
          <p className="mt-3 text-base sm:text-lg text-[#4A524C]">
            Un solo piano. Tutto incluso. Provalo gratis per un mese.
          </p>
        </div>

        <motion.div
          initial={{ y: 24, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="pricing-card"
          className="mt-10 md:mt-14 bg-white rounded-3xl border border-[#E5E3DB] shadow-lg p-8 sm:p-10 lg:p-12 max-w-2xl mx-auto"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8B6518]">
                Piano unico
              </span>
              <h3
                className="mt-1 text-2xl font-medium tracking-tight"
                style={{ fontFamily: "Work Sans, sans-serif" }}
              >
                InPalmo
              </h3>
            </div>
            <div className="text-right">
              <div className="flex items-baseline gap-1">
                <span
                  className="text-5xl font-semibold tracking-tight text-[#2E4A3B]"
                  style={{ fontFamily: "Work Sans, sans-serif" }}
                >
                  €19
                </span>
                <span className="text-base text-[#4A524C]">/mese</span>
              </div>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {items.map((it) => (
              <div key={it} className="flex items-start gap-2.5 text-[15px] text-[#1A1C19]">
                <CheckCircle2 size={18} className="text-[#2E4A3B] flex-shrink-0 mt-0.5" />
                <span>{it}</span>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <Button
              data-testid="pricing-cta"
              onClick={onLogin}
              size="lg"
              className="w-full bg-[#2E4A3B] hover:bg-[#243A2E] text-white rounded-xl h-12 text-base font-medium"
            >
              Inizia un mese gratis
            </Button>
            <p className="mt-3 text-center text-sm text-[#4A524C]">
              Nessuna carta di credito richiesta · Cancellabile in ogni momento
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
function Faq() {
  const items = [
    {
      q: "Devo avere internet nel negozio?",
      a: "Sì, la scansione AI richiede una connessione: anche il 4G/5G del tuo smartphone va benissimo. Per il resto (consultare giacenze, registrare vendite a mano) puoi continuare a usare l'app anche con segnale debole.",
    },
    {
      q: "Funziona con scontrini stropicciati o scritti a mano?",
      a: "La scansione è allenata sui formati italiani più comuni di scontrini e DDT, anche piegati o un po' rovinati. Se la calligrafia manuale è leggibile, la legge. Se proprio non riesce, puoi sempre correggere a mano prima di salvare.",
    },
    {
      q: "I miei dati sono al sicuro? E il GDPR?",
      a: "I tuoi dati restano tuoi. Sono salvati in server europei e usiamo gli stessi standard di sicurezza delle banche. Puoi esportarli o cancellarli in ogni momento.",
    },
    {
      q: "Posso usarlo su più negozi?",
      a: "Il piano base copre un punto vendita. Per più negozi con team separati scrivi a ciao@inpalmo.it: ti facciamo un'offerta dedicata.",
    },
    {
      q: "Posso esportare i dati per il commercialista?",
      a: "In qualsiasi momento. Esporti l'inventario completo o i movimenti di vendita in CSV o Excel con un clic.",
    },
    {
      q: "Devo usarlo per forza con prezzi e margini?",
      a: "No. Se vuoi puoi usare InPalmo solo per scadenze e giacenze, senza pensare ai margini. In Impostazioni c'è un interruttore: 'Mostra il margine % sui prodotti'. Lo spegni e le percentuali (+54%, ecc.) spariscono ovunque. Tieni acceso solo se ti interessa ottimizzare la marginalità.",
    },
    {
      q: "Quanto ci metto a iniziare?",
      a: "Cinque minuti. Premi 'Provalo subito senza registrarti' qui sopra: entri in una demo con dati di esempio e puoi giocare. Quando sei convinto, registri il tuo negozio.",
    },
  ];
  return (
    <section id="faq" className="bg-[#FAF9F6]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32">
        <div className="text-center">
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-[#C65D47]">
            Domande frequenti
          </span>
          <h2
            className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#1A1C19]"
            style={{ fontFamily: "Work Sans, sans-serif" }}
          >
            Le risposte alle domande che ci fanno tutti.
          </h2>
        </div>
        <Accordion type="single" collapsible data-testid="faq-accordion" className="mt-10 space-y-3">
          {items.map((item, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="bg-white border border-[#E5E3DB] rounded-2xl px-5 sm:px-6"
            >
              <AccordionTrigger className="text-left text-base sm:text-[17px] font-medium text-[#1A1C19] hover:no-underline py-5">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[15px] text-[#4A524C] leading-relaxed pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------------- Footer ---------------- */
function Footer() {
  return (
    <footer className="bg-[#1A1C19] text-white/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-[#2E4A3B] flex items-center justify-center">
                <Leaf size={18} className="text-[#A8D4B8]" />
              </div>
              <span
                className="text-xl font-semibold tracking-tight text-white"
                style={{ fontFamily: "Work Sans, sans-serif" }}
              >
                In<span className="text-[#A8D4B8]">Palmo</span>
              </span>
            </div>
            <p className="mt-4 text-sm leading-relaxed max-w-sm">
              Il magazzino digitale per i piccoli negozi alimentari italiani. Scatta, l&apos;AI fa il resto.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">Prodotto</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="#come-funziona" className="hover:text-white transition-colors">Come funziona</a></li>
              <li><a href="#funzionalita" className="hover:text-white transition-colors">Funzionalità</a></li>
              <li><a href="#prezzi" className="hover:text-white transition-colors">Prezzi</a></li>
              <li><a href="#faq" className="hover:text-white transition-colors">Domande</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">Legale</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/termini" className="hover:text-white transition-colors" data-testid="footer-terms-link">Termini di Servizio</Link></li>
              <li><a href="mailto:ciao@inpalmo.it" className="hover:text-white transition-colors">ciao@inpalmo.it</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-white/50">
          <div>© {new Date().getFullYear()} InPalmo. Tutti i diritti riservati.</div>
          <div>Fatto con cura in Italia</div>
        </div>
      </div>
    </footer>
  );
}
